import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { setStoredToken } from '../axios-config';
import { Mail, Lock, User, Phone, Building, FileText, Sparkles, ArrowLeft, ShieldCheck, AlertCircle, Loader2, Copy, Landmark, MapPin, QrCode, Download } from 'lucide-react';
import { toast } from 'sonner';
import { ConsentCheckbox, POLICY_VERSION } from '../components/legal/PolicyLayout';
import CategoryMultiSelect, { OTHER_KEY } from '../components/CategoryMultiSelect';

// Supplier membership pricing — kept in sync with backend SUPPLIER_REG_* constants.
const SUPPLIER_FEE = { base: 2499.00, gst: 449.82, total: 2948.82 };

const Register = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { register, setUser, checkAuth } = useAuth();
  const [searchParams] = useSearchParams();

  // Determine role from URL path first, then query param, default to buyer
  const getInitialRole = () => {
    if (location.pathname === '/supplier-register') return 'supplier';
    if (location.pathname === '/buyer-register') return 'buyer';
    return searchParams.get('role') || 'buyer';
  };
  const [role, setRole] = useState(getInitialRole());
  const [consent, setConsent] = useState(false);

  // Capture referral code from ?ref=CODE
  const refCode = searchParams.get('ref') || '';
  const [formData, setFormData] = useState({
    email: '', password: '', name: '',
    referral_code: refCode,
    // Common
    mobile: '', city: '', district: '', state: '',
    // Buyer
    organization_name: '', contact_person: '', phone: '', buyer_type: '', gst_number: '', address: '',
    // Supplier
    company_name: '', business_type: '', categories: [], service_state: '', service_area: '',
    experience: '', pan: '', website: '', description: '', bid_capacity: '', gst_registered: null
  });
  const [loading, setLoading] = useState(false);

  // Buyer membership pricing — resolved server-side via /api/pricing/buyer so it
  // honours the Founding Member campaign (currently ₹4,999 + GST ₹899.82 = ₹5,898.82
  // open until the first 100 buyer slots fill — no calendar end date). Falls back to
  // regular pricing once the campaign is disabled/slots exhausted. Regular pricing
  // (₹9,999 + GST ₹1,799.82 = ₹11,798.82) is only used as an initial placeholder
  // while the fetch is in flight.
  const [buyerPricing, setBuyerPricing] = useState({
    base_amount: 9999.00, gst_amount: 1799.82, total_amount: 11798.82,
    plan_label: 'Buyer Yearly Subscription', is_founding_pricing: false,
    campaign_status: 'loading',
  });

  useEffect(() => {
    if (role !== 'buyer') return;
    let cancelled = false;
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/pricing/buyer`)
      .then(({ data }) => { if (!cancelled) setBuyerPricing(data); })
      .catch(() => { /* keep placeholder — resolver returns regular pricing as fallback anyway */ });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role]);

  // Legacy shape used by the JSX below.
  const BUYER_FEE = { base: buyerPricing.base_amount, gst: buyerPricing.gst_amount, total: buyerPricing.total_amount };

  // Supplier paid-registration step machine: 'form' → 'review' → (Razorpay) → success
  const [supplierStep, setSupplierStep] = useState('form');
  const [pendingOrder, setPendingOrder] = useState(null); // { order, razorpay_key_id, mode, prefill }

  // Buyer registration step machine: 'form' → 'payment_method' → 'manual_details' → success
  //                                                            → (Razorpay) → success
  const [buyerStep, setBuyerStep] = useState('form');
  const [buyerPaymentMethod, setBuyerPaymentMethod] = useState(null); // 'razorpay' | 'cheque' | 'neft' | 'rtgs'
  const [manualPayment, setManualPayment] = useState({
    bank_name: '', reference_number: '', transaction_date: '', amount: '', remarks: '',
  });

  const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

  // ─────────────────────────────────────────────
  // Buyer (unchanged) — direct registration.
  // Supplier — payment-gated registration.
  // ─────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!consent) {
      toast.error('Please accept the Terms, Privacy Policy and Data Protection Policy to continue.');
      return;
    }

    if (role === 'supplier') {
      const cats = (formData.categories || []).filter(Boolean);
      if (cats.length === 0) {
        toast.error('Please select at least one Service / Work Category.');
        return;
      }
      if (cats.includes(OTHER_KEY)) {
        toast.error('Please specify and add your "Other" category before continuing.');
        return;
      }
      setSupplierStep('review');
      return;
    }

    // BUYER — move to payment-method selection before creating anything.
    setBuyerStep('payment_method');
  };

  // ─────────────────────────────────────────────
  // Buyer · Razorpay flow
  // ─────────────────────────────────────────────
  const initiateBuyerRazorpay = async () => {
    if (typeof window.Razorpay === 'undefined') {
      toast.error('Razorpay checkout failed to load. Please refresh and try again.');
      return;
    }
    setLoading(true);
    try {
      const payload = {
        ...formData,
        role: 'buyer',
        consent_accepted: true,
        consent_policy_version: POLICY_VERSION,
      };
      const { data } = await axios.post(`${API}/auth/buyer/initiate-registration`, payload, { withCredentials: true });
      if (!data.ok) {
        toast.error(data.message || 'Could not start payment.');
        setLoading(false);
        return;
      }
      const opts = {
        key: data.razorpay_key_id,
        amount: data.order.amount,
        currency: data.order.currency,
        name: 'ProcureOne Ultra',
        description: data.plan_label + (data.mode === 'test' ? ' (TEST MODE)' : ''),
        order_id: data.order.id,
        prefill: data.prefill || { name: formData.name, email: formData.email, contact: formData.mobile },
        theme: { color: '#0A1F44' },
        handler: async (response) => {
          try {
            const { data: v } = await axios.post(`${API}/auth/buyer/complete-registration`, {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            }, { withCredentials: true });
            if (v?.access_token) setStoredToken(v.access_token);
            setUser({ id: v.id, email: v.email, name: v.name, role: v.role, approved: v.approved, is_founding: v.is_founding });
            try { await checkAuth(); } catch { /* ignore */ }
            toast.success(`Payment successful! Invoice ${v.invoice_no} emailed to ${v.email}.`);
            navigate('/buyer-dashboard');
          } catch (err) {
            const detail = err.response?.data?.detail;
            const msg = typeof detail === 'string' ? detail : 'Payment verification failed. Please contact support with your payment reference.';
            toast.error(msg);
            setLoading(false);
          }
        },
        modal: {
          ondismiss: () => {
            setLoading(false);
            toast('Payment cancelled. You can retry or choose a manual method.', { description: 'No account was created and no money was charged.' });
          },
        },
      };
      const rzp = new window.Razorpay(opts);
      rzp.on('payment.failed', () => {
        setLoading(false);
        toast.error('Payment failed. Please retry — no account was created.');
      });
      rzp.open();
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : 'Failed to create payment order';
      toast.error(msg);
      setLoading(false);
    }
  };

  // ─────────────────────────────────────────────
  // Buyer · Manual (Cheque / NEFT / RTGS) submit
  // ─────────────────────────────────────────────
  const submitBuyerManualPayment = async () => {
    if (!manualPayment.bank_name.trim()) { toast.error('Bank name is required'); return; }
    if (!manualPayment.reference_number.trim()) { toast.error(buyerPaymentMethod === 'cheque' ? 'Cheque number is required' : 'UTR / Reference number is required'); return; }
    if (!manualPayment.transaction_date) { toast.error(buyerPaymentMethod === 'cheque' ? 'Cheque date is required' : 'Transaction date is required'); return; }
    if (!manualPayment.amount || Number(manualPayment.amount) <= 0) { toast.error('Amount must be greater than zero'); return; }

    setLoading(true);
    try {
      const payload = {
        registration: {
          ...formData,
          role: 'buyer',
          consent_accepted: true,
          consent_policy_version: POLICY_VERSION,
        },
        payment: {
          method: buyerPaymentMethod,
          bank_name: manualPayment.bank_name.trim(),
          reference_number: manualPayment.reference_number.trim(),
          transaction_date: manualPayment.transaction_date,
          amount: Number(manualPayment.amount),
          remarks: (manualPayment.remarks || '').trim(),
        },
      };
      const { data } = await axios.post(`${API}/auth/buyer/register-with-manual-payment`, payload, { withCredentials: true });
      if (data?.access_token) setStoredToken(data.access_token);
      setUser({ id: data.id, email: data.email, name: data.name, role: data.role, approved: data.approved, membership_status: data.membership_status });
      try { await checkAuth(); } catch { /* ignore */ }
      toast.success('Registration submitted! Your payment is awaiting verification by the ProcureOne Ultra Admin Team.', {
        description: `Reference: ${manualPayment.reference_number.trim()}`, duration: 6000,
      });
      navigate('/buyer-dashboard');
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : (Array.isArray(detail) ? detail.map(e => e.msg).join(', ') : 'Failed to submit registration');
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  // ─────────────────────────────────────────────
  // Supplier Razorpay flow
  // ─────────────────────────────────────────────
  const initiateSupplierPayment = async () => {
    if (typeof window.Razorpay === 'undefined') {
      toast.error('Razorpay checkout failed to load. Please refresh and try again.');
      return;
    }
    setLoading(true);
    try {
      const cats = (formData.categories || []).filter(c => c && c !== OTHER_KEY);
      const payload = {
        ...formData,
        // Backend RegisterRequest still accepts legacy `category` (single) AND `categories` (list).
        // Send both for backward compat — primary category = first selected.
        category: cats[0] || '',
        categories: cats,
        role: 'supplier',
        consent_accepted: true,
        consent_policy_version: POLICY_VERSION,
      };
      const { data } = await axios.post(`${API}/auth/supplier/initiate-registration`, payload, { withCredentials: true });
      if (!data.ok) {
        toast.error(data.message || 'Could not start payment.');
        setLoading(false);
        return;
      }
      setPendingOrder(data);
      openRazorpay(data);
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : (Array.isArray(detail) ? detail.map(e => e.msg || JSON.stringify(e)).join(', ') : 'Failed to create payment order');
      toast.error(msg);
      setLoading(false);
    }
  };

  const openRazorpay = (orderData) => {
    const opts = {
      key: orderData.razorpay_key_id,
      amount: orderData.order.amount,
      currency: orderData.order.currency,
      name: 'ProcureOne Ultra',
      description: orderData.plan_label + (orderData.mode === 'test' ? ' (TEST MODE)' : ''),
      order_id: orderData.order.id,
      prefill: orderData.prefill || { name: formData.name, email: formData.email, contact: formData.mobile },
      theme: { color: '#0A1F44' },
      handler: async (response) => {
        try {
          const { data } = await axios.post(`${API}/auth/supplier/complete-registration`, {
            razorpay_order_id: response.razorpay_order_id,
            razorpay_payment_id: response.razorpay_payment_id,
            razorpay_signature: response.razorpay_signature,
          }, { withCredentials: true });
          // Token + user are now active. Hop into the supplier dashboard.
          if (data?.access_token) setStoredToken(data.access_token);
          setUser({ id: data.id, email: data.email, name: data.name, role: data.role, approved: data.approved });
          // Re-sync from /auth/me to populate any server-side context (subscription, etc.).
          try { await checkAuth(); } catch { /* ignore */ }
          toast.success(`Payment successful! Invoice ${data.invoice_no} emailed to ${data.email}.`);
          navigate('/supplier-dashboard');
        } catch (err) {
          const detail = err.response?.data?.detail;
          const msg = typeof detail === 'string' ? detail : 'Payment verification failed. Please contact support@plusultrainnovation.com with your payment reference.';
          toast.error(msg);
          setLoading(false);
        }
      },
      modal: {
        ondismiss: () => {
          setLoading(false);
          toast('Payment cancelled. You can retry without re-entering your details.', { description: 'No account was created and no money was charged.' });
        },
      },
    };
    const rzp = new window.Razorpay(opts);
    rzp.on('payment.failed', () => {
      setLoading(false);
      toast.error('Payment failed. Please retry — no account was created.');
    });
    rzp.open();
  };

  const inputClass = "w-full px-4 py-2 rounded-md border border-input bg-background focus:ring-2 focus:ring-ring";


  // ─────────────────────────────────────────────
  // BUYER · STEP 2 — Membership + Payment Method selection
  // ─────────────────────────────────────────────
  if (role === 'buyer' && buyerStep === 'payment_method') {
    const methods = [
      { key: 'razorpay', label: 'Razorpay', sub: 'UPI · Card · Netbanking · Wallets', desc: 'Instant activation. GST invoice emailed immediately.', badge: 'INSTANT' },
      { key: 'cheque', label: 'Cheque', sub: 'Physical cheque deposit', desc: 'Activation after admin verifies the cheque credit (typically 3–5 working days).' },
      { key: 'neft', label: 'NEFT', sub: 'Bank transfer via NEFT', desc: 'Activation after admin verifies the UTR against bank statement.' },
      { key: 'rtgs', label: 'RTGS', sub: 'Real-time gross settlement', desc: 'Activation after admin verifies the UTR against bank statement.' },
    ];
    return (
      <div className="min-h-screen flex items-center justify-center bg-background px-4 sm:px-6 py-8 sm:py-12">
        <div className="w-full max-w-3xl">
          <div className="mb-6">
            <button type="button" onClick={() => setBuyerStep('form')} className="inline-flex items-center text-sm font-medium text-muted-foreground hover:text-primary" data-testid="buyer-payment-back-btn">
              <ArrowLeft className="h-4 w-4 mr-1.5" /> Edit registration details
            </button>
          </div>
          <div className="text-center mb-6">
            <div className="flex items-center justify-center space-x-2 mb-3">
              <Sparkles className="h-9 w-9 text-gold" />
              <div>
                <div className="text-xl font-bold text-navy">ProcureOne Ultra</div>
                <div className="text-xs text-muted-foreground">by Plus Ultra Innovation</div>
              </div>
            </div>
            <h1 className="text-2xl font-semibold text-navy" data-testid="buyer-plan-heading">{buyerPricing.plan_label} · Choose Payment Method</h1>
            <p className="text-muted-foreground mt-1 text-sm">Pick how you&apos;d like to pay. Razorpay activates your account instantly; manual methods activate on admin verification.</p>
            {buyerPricing.is_founding_pricing && (
              <div className="inline-flex items-center gap-1.5 mt-2 px-3 py-1 rounded-full bg-gold/25 text-navy text-xs font-bold" data-testid="buyer-founding-badge">
                <Sparkles className="h-3.5 w-3.5" /> Founding Member Pricing · {buyerPricing.slots_remaining} of {buyerPricing.max_slots} slots remaining
              </div>
            )}
          </div>

          <div className="bg-card border border-border rounded-lg p-6 shadow-sm mb-4" data-testid="buyer-plan-summary">
            <div className="text-xs uppercase tracking-wider font-bold text-muted-foreground mb-1">Registering as</div>
            <div className="text-lg font-semibold text-navy">{formData.organization_name || formData.name}</div>
            <div className="text-sm text-muted-foreground mb-4">{formData.email}</div>
            <div className="space-y-2 border-t border-border pt-3">
              <Row label={`${buyerPricing.plan_label} (12 months)`} value={`₹${BUYER_FEE.base.toLocaleString('en-IN', { minimumFractionDigits: 2 })}`} testid="buyer-fee-base-row" />
              <Row label="GST @ 18%" value={`₹${BUYER_FEE.gst.toLocaleString('en-IN', { minimumFractionDigits: 2 })}`} testid="buyer-fee-gst-row" />
              <div className="border-t border-border pt-2"></div>
              <Row label="Total Payable" value={`₹${BUYER_FEE.total.toLocaleString('en-IN', { minimumFractionDigits: 2 })}`} bold testid="buyer-fee-total-row" />
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-3 mb-5" data-testid="buyer-payment-methods">
            {methods.map(m => (
              <button
                key={m.key}
                type="button"
                onClick={() => { setBuyerPaymentMethod(m.key); setManualPayment(mp => ({ ...mp, amount: BUYER_FEE.total })); }}
                className={`text-left p-4 rounded-lg border-2 transition ${buyerPaymentMethod === m.key ? 'border-orange bg-orange/5' : 'border-border hover:border-navy/40'}`}
                data-testid={`buyer-pay-${m.key}`}
              >
                <div className="flex items-center justify-between mb-1">
                  <span className="font-bold text-navy">{m.label}</span>
                  {m.badge && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-gold/30 text-navy">{m.badge}</span>}
                </div>
                <div className="text-xs text-muted-foreground mb-2">{m.sub}</div>
                <div className="text-[11px] text-muted-foreground">{m.desc}</div>
              </button>
            ))}
          </div>

          {buyerPaymentMethod === 'razorpay' && (
            <button
              type="button"
              onClick={initiateBuyerRazorpay}
              disabled={loading}
              className="w-full px-4 py-3 rounded-md bg-gold hover:bg-orange text-navy hover:text-white font-bold text-sm inline-flex items-center justify-center disabled:opacity-60"
              data-testid="buyer-pay-razorpay-btn"
            >
              {loading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Opening secure checkout…</>
                : <><Lock className="h-4 w-4 mr-2" /> Pay ₹{BUYER_FEE.total.toLocaleString('en-IN', { minimumFractionDigits: 2 })} via Razorpay</>}
            </button>
          )}
          {(buyerPaymentMethod === 'cheque' || buyerPaymentMethod === 'neft' || buyerPaymentMethod === 'rtgs') && (
            <button
              type="button"
              onClick={() => setBuyerStep('manual_details')}
              className="w-full px-4 py-3 rounded-md bg-navy hover:opacity-90 text-white font-bold text-sm"
              data-testid="buyer-continue-manual-btn"
            >
              Continue with {buyerPaymentMethod.toUpperCase()} →
            </button>
          )}
          {!buyerPaymentMethod && (
            <div className="text-xs text-muted-foreground text-center" data-testid="buyer-payment-hint">Please choose a payment method to continue.</div>
          )}
        </div>
      </div>
    );
  }

  // ─────────────────────────────────────────────
  // BUYER · STEP 3 — Manual payment details (Cheque / NEFT / RTGS)
  // ─────────────────────────────────────────────
  if (role === 'buyer' && buyerStep === 'manual_details') {
    const isCheque = buyerPaymentMethod === 'cheque';
    const upperMethod = (buyerPaymentMethod || '').toUpperCase();
    const copy = async (label, value) => {
      const ok = await copyToClipboard(value);
      if (ok) toast.success(`${label} copied`);
      else toast.error('Copy failed — please copy manually');
    };
    return (
      <div className="min-h-screen flex items-start sm:items-center justify-center bg-background px-4 sm:px-6 py-8 sm:py-12">
        <div className="w-full max-w-3xl">
          <div className="mb-6">
            <button type="button" onClick={() => setBuyerStep('payment_method')} className="inline-flex items-center text-sm font-medium text-muted-foreground hover:text-primary" data-testid="buyer-manual-back-btn">
              <ArrowLeft className="h-4 w-4 mr-1.5" /> Change payment method
            </button>
          </div>
          <div className="text-center mb-6">
            <div className="flex items-center justify-center space-x-2 mb-3">
              <Sparkles className="h-9 w-9 text-gold" />
              <div>
                <div className="text-xl font-bold text-navy">ProcureOne Ultra</div>
                <div className="text-xs text-muted-foreground">by Plus Ultra Innovation</div>
              </div>
            </div>
            <h1 className="text-2xl font-semibold text-navy">{upperMethod} Payment Details</h1>
            <p className="text-muted-foreground mt-1 text-sm">Enter your {isCheque ? 'cheque' : 'bank transfer'} details. Your account will be created and activated once our Admin team verifies the payment.</p>
          </div>

          {/* Payment Instructions card — company bank details for Cheque / NEFT / RTGS.
              Design: white base + lavender surface + navy headings + gold highlights. */}
          <div
            className="mb-6 rounded-xl border border-navy/15 bg-white shadow-md overflow-hidden"
            style={{ '--lavender': '#F3F0FF' }}
            data-testid="payment-instructions-card"
          >
            <div className="px-5 py-4 bg-navy text-white flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-gold/25 flex items-center justify-center">
                <Landmark className="h-5 w-5 text-gold" />
              </div>
              <div>
                <div className="text-sm font-bold tracking-wide uppercase">Pay ProcureOne Ultra — {isCheque ? 'Cheque Deposit Instructions' : `${upperMethod} Transfer Instructions`}</div>
                <div className="text-[11px] opacity-80">Use these official company banking details. Do not send payments to any other account.</div>
              </div>
            </div>

            <div className="p-5" style={{ background: 'linear-gradient(180deg, #F3F0FF 0%, #FFFFFF 60%)' }}>
              <div className="grid sm:grid-cols-2 gap-4">
                <InfoRow label="Pay To" value="Plus Ultra Innovation" testid="pi-payto" bold />
                <InfoRow label="Bank" value="Union Bank of India" testid="pi-bank" icon={<Landmark className="h-3.5 w-3.5 text-gold" />} />
                <InfoRow label="Branch" value="Ghodbunder Road, Thane" testid="pi-branch" icon={<MapPin className="h-3.5 w-3.5 text-gold" />} />
                <InfoRow label="Account Type" value="Current Account" testid="pi-actype" />
                <InfoRow
                  label="Account Number"
                  value="549701010051194"
                  testid="pi-account"
                  mono
                  action={{ icon: <Copy className="h-3.5 w-3.5" />, label: 'Copy', onClick: () => copy('Account number', '549701010051194'), testid: 'copy-account-btn' }}
                />
                <InfoRow
                  label="IFSC Code"
                  value="UBIN0554979"
                  testid="pi-ifsc"
                  mono
                  action={{ icon: <Copy className="h-3.5 w-3.5" />, label: 'Copy', onClick: () => copy('IFSC code', 'UBIN0554979'), testid: 'copy-ifsc-btn' }}
                />
                <InfoRow label="GSTIN" value="27EYMPS1491H1ZT" testid="pi-gstin" mono />
                <InfoRow label="Support" value="info@plusultrainnovation.com" testid="pi-support" icon={<Mail className="h-3.5 w-3.5 text-gold" />} />
              </div>

              <div className="mt-4 p-3 rounded-lg border border-gold/40 bg-gold/10 text-[12px] text-navy leading-relaxed" data-testid="pi-note">
                <strong>Payment Note:</strong> Please transfer the exact membership amount using the bank details above. After completing the payment, enter your {isCheque ? 'Cheque Number' : 'UTR Reference Number'} and submit your registration. Your membership will be activated after payment verification by the ProcureOne Ultra Admin Team.
              </div>

              {/* Placeholder actions — PDF + QR are Phase-2 (not implemented today). */}
              <div className="mt-4 flex flex-wrap gap-2 text-xs">
                <button
                  type="button"
                  disabled
                  title="Coming soon"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded border border-navy/20 text-navy/50 cursor-not-allowed"
                  data-testid="pi-download-pdf-placeholder"
                >
                  <Download className="h-3.5 w-3.5" /> Download Payment Instructions (PDF)
                  <span className="ml-1 text-[10px] font-bold uppercase text-orange">Soon</span>
                </button>
                <button
                  type="button"
                  disabled
                  title="Coming soon"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded border border-navy/20 text-navy/50 cursor-not-allowed"
                  data-testid="pi-qr-placeholder"
                >
                  <QrCode className="h-3.5 w-3.5" /> Show Payment QR
                  <span className="ml-1 text-[10px] font-bold uppercase text-orange">Soon</span>
                </button>
              </div>
            </div>
          </div>

          <div className="bg-card border border-border rounded-lg p-6 shadow-lg space-y-4" data-testid="buyer-manual-form">
            <div className="rounded-md bg-gold/10 border border-gold/30 p-3 text-xs text-navy">
              <ShieldCheck className="h-4 w-4 inline mr-1.5 text-gold" />
              <strong>Amount:</strong> ₹{BUYER_FEE.total.toLocaleString('en-IN', { minimumFractionDigits: 2 })} ({buyerPricing.plan_label} incl. 18% GST). Please transfer this exact amount and enter the details below.
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Bank Name *</label>
              <input type="text" value={manualPayment.bank_name} onChange={e => setManualPayment({ ...manualPayment, bank_name: e.target.value })} className={inputClass} placeholder="e.g. HDFC Bank" data-testid="manual-bank-input" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">{isCheque ? 'Cheque Number *' : 'UTR / Reference Number *'}</label>
              <input type="text" value={manualPayment.reference_number} onChange={e => setManualPayment({ ...manualPayment, reference_number: e.target.value })} className={inputClass} placeholder={isCheque ? 'e.g. 123456' : 'e.g. HDFCN1234567890'} data-testid="manual-reference-input" />
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">{isCheque ? 'Cheque Date *' : 'Transaction Date *'}</label>
                <input type="date" value={manualPayment.transaction_date} onChange={e => setManualPayment({ ...manualPayment, transaction_date: e.target.value })} className={inputClass} data-testid="manual-date-input" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Amount (₹) *</label>
                <input type="number" step="0.01" value={manualPayment.amount} onChange={e => setManualPayment({ ...manualPayment, amount: e.target.value })} className={inputClass} data-testid="manual-amount-input" />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Remarks (optional)</label>
              <textarea value={manualPayment.remarks} onChange={e => setManualPayment({ ...manualPayment, remarks: e.target.value })} rows={2} className={inputClass} placeholder="e.g. Deposit branch, cheque leaf # etc." data-testid="manual-remarks-input" />
            </div>
            <button
              type="button"
              onClick={submitBuyerManualPayment}
              disabled={loading}
              className="w-full px-4 py-3 rounded-md bg-navy hover:opacity-90 text-white font-bold text-sm disabled:opacity-50 inline-flex items-center justify-center"
              data-testid="manual-submit-btn"
            >
              {loading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Submitting…</> : `Submit Registration & ${upperMethod} Details`}
            </button>
            <p className="text-[11px] text-muted-foreground text-center">
              Your account will be created immediately so you can log in and track status.
              Membership activates on admin verification (typically 3–5 working days).
            </p>
          </div>
        </div>
      </div>
    );
  }


  // ─────────────────────────────────────────────
  // SUPPLIER · STEP 2 — Fee breakup + Pay
  // ─────────────────────────────────────────────
  if (role === 'supplier' && supplierStep === 'review') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background px-4 sm:px-6 py-8 sm:py-12">
        <div className="w-full max-w-2xl">
          <div className="mb-6">
            <button
              type="button"
              onClick={() => setSupplierStep('form')}
              className="inline-flex items-center text-sm font-medium text-muted-foreground hover:text-primary transition-colors"
              data-testid="supplier-review-back-btn"
            >
              <ArrowLeft className="h-4 w-4 mr-1.5" /> Edit registration details
            </button>
          </div>

          <div className="text-center mb-6">
            <div className="flex items-center justify-center space-x-2 mb-3">
              <Sparkles className="h-9 w-9 text-gold" />
              <div>
                <div className="text-xl font-bold text-navy">ProcureOne Ultra</div>
                <div className="text-xs text-muted-foreground">by Plus Ultra Innovation</div>
              </div>
            </div>
            <h1 className="text-2xl font-semibold text-navy">Supplier Yearly Subscription · Payment Summary</h1>
            <p className="text-muted-foreground mt-1 text-sm">Review the yearly subscription charges and pay securely via Razorpay to activate your account instantly.</p>
          </div>

          <div className="bg-card border border-border rounded-lg p-6 shadow-lg" data-testid="supplier-fee-breakup">
            <div className="mb-5">
              <div className="text-xs uppercase tracking-wider font-bold text-muted-foreground mb-1">Registering As</div>
              <div className="text-lg font-semibold text-navy" data-testid="supplier-review-company">{formData.company_name || formData.name}</div>
              <div className="text-sm text-muted-foreground" data-testid="supplier-review-email">{formData.email}</div>
            </div>

            <div className="space-y-3 mb-5">
              <Row label="Yearly Subscription (12 months)" value={`₹${SUPPLIER_FEE.base.toLocaleString('en-IN', { minimumFractionDigits: 2 })}`} testid="fee-base-row" />
              <Row label="GST @ 18%" value={`₹${SUPPLIER_FEE.gst.toLocaleString('en-IN', { minimumFractionDigits: 2 })}`} testid="fee-gst-row" />
              <div className="border-t border-border pt-3"></div>
              <Row label="Total Payable" value={`₹${SUPPLIER_FEE.total.toLocaleString('en-IN', { minimumFractionDigits: 2 })}`} bold testid="fee-total-row" />
            </div>

            <div className="rounded-md bg-gold/10 border border-gold/30 p-3 mb-5 text-xs text-navy" data-testid="supplier-fee-note">
              <ShieldCheck className="h-4 w-4 inline mr-1.5 text-gold" />
              On successful payment your account is <strong>activated immediately</strong>. A GST-compliant tax invoice will be emailed to <strong>{formData.email}</strong> and saved under your Invoices section.
            </div>

            <button
              type="button"
              onClick={initiateSupplierPayment}
              disabled={loading}
              className="w-full px-4 py-3 rounded-md bg-gold hover:bg-orange text-navy hover:text-white font-bold text-sm transition-colors disabled:opacity-60 inline-flex items-center justify-center"
              data-testid="supplier-pay-now-btn"
            >
              {loading
                ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Opening secure checkout…</>
                : <><Lock className="h-4 w-4 mr-2" /> Pay ₹{SUPPLIER_FEE.total.toLocaleString('en-IN', { minimumFractionDigits: 2 })} via Razorpay</>}
            </button>

            <p className="text-[11px] text-muted-foreground text-center mt-3 inline-flex items-center justify-center w-full">
              <ShieldCheck className="h-3 w-3 mr-1" /> Payments handled by Razorpay (PCI DSS Level 1). 256-bit SSL.
            </p>
          </div>

          <div className="text-[11px] text-muted-foreground mt-4 text-center" data-testid="supplier-review-retry-hint">
            If payment fails or is cancelled, your account will <strong>not</strong> be created. You can retry without re-entering your details.
          </div>
        </div>
      </div>
    );
  }

  // ─────────────────────────────────────────────
  // STEP 1 — Form (Buyer + Supplier share the same form)
  // ─────────────────────────────────────────────
  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-6 py-12">
      <div className="w-full max-w-3xl">
        <div className="mb-6">
          <Link
            to="/"
            className="inline-flex items-center text-sm font-medium text-muted-foreground hover:text-primary transition-colors"
            data-testid="register-back-home-link"
          >
            <ArrowLeft className="h-4 w-4 mr-1.5" /> Back to Home
          </Link>
        </div>
        <div className="text-center mb-8">
          <div className="flex items-center justify-center space-x-3 mb-4">
            <img
              src="/plus-ultra-logo.webp"
              alt="Plus Ultra Innovation"
              className="h-14 w-14 rounded-full object-contain bg-white ring-1 ring-gold/50"
              data-testid="register-logo-img"
            />
            <div>
              <div className="text-2xl font-bold text-navy">ProcureOne Ultra</div>
              <div className="text-xs text-muted-foreground">by Plus Ultra Innovation</div>
            </div>
          </div>
          <h1 className="text-2xl font-semibold text-navy">Create Your Account</h1>
          <p className="text-muted-foreground mt-2">Be part of the digital revolution</p>
          {refCode && (
            <div className="inline-block mt-3 px-3 py-1 rounded-full bg-orange/20 text-orange text-xs font-bold uppercase tracking-wider" data-testid="referral-code-banner">
              Referred by code: {refCode}
            </div>
          )}
        </div>

        <div className="bg-card border border-border rounded-lg p-8 shadow-lg">
          {/* Role Selection */}
          <div className="mb-6">
            <label className="block text-sm font-medium mb-3">I am a</label>
            <div className="grid grid-cols-2 gap-4">
              <button type="button" onClick={() => setRole('buyer')} className={`p-4 rounded-lg border-2 transition-colors ${role === 'buyer' ? 'border-navy bg-navy/5' : 'border-border hover:border-navy/50'}`} data-testid="role-buyer-btn">
                <Building className="h-6 w-6 mx-auto mb-2 text-navy" />
                <div className="font-medium">Buyer</div>
                <div className="text-xs text-muted-foreground">Society / Company / Institution</div>
              </button>
              <button type="button" onClick={() => setRole('supplier')} className={`p-4 rounded-lg border-2 transition-colors ${role === 'supplier' ? 'border-orange bg-orange/5' : 'border-border hover:border-orange/50'}`} data-testid="role-supplier-btn">
                <FileText className="h-6 w-6 mx-auto mb-2 text-orange" />
                <div className="font-medium">Supplier</div>
                <div className="text-xs text-muted-foreground">Contractor / Manufacturer / Trader</div>
              </button>
            </div>
          </div>

          {/* Paid-membership notice for suppliers — sets the right expectation BEFORE they fill the form. */}
          {role === 'supplier' && (
            <div className="mb-6 rounded-md border border-orange/40 bg-orange/5 p-4 text-sm text-navy" data-testid="supplier-paid-membership-banner">
              <div className="flex items-start">
                <AlertCircle className="h-5 w-5 text-orange mr-2 flex-shrink-0 mt-0.5" />
                <div>
                  <div className="font-bold mb-1">Supplier Yearly Subscription · Paid Activation</div>
                  <div className="text-xs text-muted-foreground leading-relaxed">
                    Supplier registration requires a yearly subscription of <strong>₹{SUPPLIER_FEE.base.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</strong> + 18% GST (<strong>₹{SUPPLIER_FEE.gst.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</strong>) = <strong>₹{SUPPLIER_FEE.total.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</strong> total per year. You will be redirected to Razorpay Checkout after clicking <em>Create Account</em>. Your account is <strong>auto-activated</strong> on successful payment — no admin review required.
                  </div>
                </div>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Optional Referral Code */}
            <div>
              <label className="block text-sm font-medium mb-2">Referral Code (Optional)</label>
              <input
                type="text"
                value={formData.referral_code}
                onChange={(e) => setFormData({ ...formData, referral_code: e.target.value.toUpperCase() })}
                placeholder="e.g. PUI-RP-XXXXXX"
                className={inputClass}
                data-testid="register-referral-input"
              />
              {formData.referral_code && (
                <p className="text-xs text-orange mt-1">Linked to partner referral code: <strong>{formData.referral_code}</strong></p>
              )}
            </div>

            {/* Common Fields */}
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Full Name *</label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                  <input type="text" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required className={`pl-10 ${inputClass}`} data-testid="register-name-input" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Email *</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                  <input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} required className={`pl-10 ${inputClass}`} data-testid="register-email-input" />
                </div>
                <p className="text-xs text-muted-foreground mt-1.5 leading-relaxed">
                  This email will be used for all future communication, account access, and sharing of <strong>invoices &amp; GST receipts</strong>. Please ensure it is correct.
                </p>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Mobile Number *</label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                  <input type="tel" value={formData.mobile} onChange={(e) => setFormData({ ...formData, mobile: e.target.value })} required className={`pl-10 ${inputClass}`} data-testid="register-mobile-input" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Password *</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                  <input type="password" value={formData.password} onChange={(e) => setFormData({ ...formData, password: e.target.value })} required minLength={6} className={`pl-10 ${inputClass}`} data-testid="register-password-input" />
                </div>
              </div>
            </div>

            {/* Location */}
            <div className="grid md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">City *</label>
                <input type="text" value={formData.city} onChange={(e) => setFormData({ ...formData, city: e.target.value })} required className={inputClass} data-testid="register-city-input" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">District *</label>
                <input type="text" value={formData.district} onChange={(e) => setFormData({ ...formData, district: e.target.value })} required className={inputClass} data-testid="register-district-input" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">State *</label>
                <input type="text" value={formData.state} onChange={(e) => setFormData({ ...formData, state: e.target.value })} required className={inputClass} data-testid="register-state-input" />
              </div>
            </div>

            {/* Buyer Fields */}
            {role === 'buyer' && (
              <>
                <div>
                  <label className="block text-sm font-medium mb-2">Organization Name *</label>
                  <input type="text" value={formData.organization_name} onChange={(e) => setFormData({ ...formData, organization_name: e.target.value })} required className={inputClass} data-testid="buyer-org-input" />
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Buyer Type *</label>
                    <select value={formData.buyer_type} onChange={(e) => setFormData({ ...formData, buyer_type: e.target.value })} required className={inputClass} data-testid="buyer-type-select">
                      <option value="">Select Type</option>
                      <option value="Co-operative Housing Society">Co-operative Housing Society</option>
                      <option value="Facility / Property Management Company">Facility / Property Management Company</option>
                    </select>
                    <p className="text-[10px] text-muted-foreground mt-1">V1 supports Co-operative Housing Societies and Facility/Property Management Companies. Additional categories coming in V2.</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">GST Number (Optional)</label>
                    <input type="text" value={formData.gst_number} onChange={(e) => setFormData({ ...formData, gst_number: e.target.value })} className={inputClass} data-testid="buyer-gst-input" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Address (Optional)</label>
                  <textarea value={formData.address} onChange={(e) => setFormData({ ...formData, address: e.target.value })} rows={2} className={inputClass} data-testid="buyer-address-input" />
                </div>
              </>
            )}

            {/* Supplier Fields */}
            {role === 'supplier' && (
              <>
                <div>
                  <label className="block text-sm font-medium mb-2">Company Name *</label>
                  <input type="text" value={formData.company_name} onChange={(e) => setFormData({ ...formData, company_name: e.target.value })} required className={inputClass} data-testid="supplier-company-input" />
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Business Type *</label>
                    <select value={formData.business_type} onChange={(e) => setFormData({ ...formData, business_type: e.target.value })} required className={inputClass} data-testid="supplier-businesstype-select">
                      <option value="">Select Type</option>
                      <option value="Proprietorship">Proprietorship</option>
                      <option value="Partnership">Partnership</option>
                      <option value="LLP">LLP</option>
                      <option value="Private Limited">Private Limited</option>
                      <option value="OPC">OPC (One Person Company)</option>
                      <option value="Individual Service Provider">Individual Service Provider</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Service Area</label>
                    <input type="text" value={formData.service_area} onChange={(e) => setFormData({ ...formData, service_area: e.target.value })} placeholder="e.g., Mumbai, Pune, Nashik" className={inputClass} data-testid="supplier-servicearea-input" />
                  </div>
                </div>

                <div>
                  <CategoryMultiSelect
                    label="Service / Work Categories"
                    required
                    multiple
                    allowOther
                    testid="supplier-categories"
                    value={formData.categories}
                    onChange={(next) => setFormData({ ...formData, categories: next })}
                    placeholder="Select one or more categories you serve…"
                  />
                  <p className="text-[11px] text-muted-foreground mt-1.5">
                    You will appear in searches, automatic tender matching and Quick Quotation invitations for every selected category.
                  </p>
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Experience</label>
                    <input type="text" value={formData.experience} onChange={(e) => setFormData({ ...formData, experience: e.target.value })} placeholder="e.g., 10 years" className={inputClass} data-testid="supplier-experience-input" />
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">GST Registered?</label>
                    <div className="flex items-center gap-3" data-testid="supplier-gst-toggle">
                      <label className="inline-flex items-center text-sm cursor-pointer">
                        <input type="radio" name="gst_registered" checked={formData.gst_registered === true} onChange={() => setFormData({ ...formData, gst_registered: true })} className="mr-1.5" data-testid="supplier-gst-yes" /> Yes
                      </label>
                      <label className="inline-flex items-center text-sm cursor-pointer">
                        <input type="radio" name="gst_registered" checked={formData.gst_registered === false} onChange={() => setFormData({ ...formData, gst_registered: false, gst_number: '' })} className="mr-1.5" data-testid="supplier-gst-no" /> No (Unregistered)
                      </label>
                    </div>
                  </div>
                  {formData.gst_registered === true ? (
                    <div>
                      <label className="block text-sm font-medium mb-2">GST Number *</label>
                      <input type="text" value={formData.gst_number} onChange={(e) => setFormData({ ...formData, gst_number: e.target.value })} className={inputClass} data-testid="supplier-gst-input" />
                    </div>
                  ) : (
                    <div>
                      <label className="block text-sm font-medium mb-2">Website (Optional)</label>
                      <input type="url" value={formData.website} onChange={(e) => setFormData({ ...formData, website: e.target.value })} placeholder="https://" className={inputClass} data-testid="supplier-website-input" />
                    </div>
                  )}
                </div>

                <div className="text-[11px] text-muted-foreground bg-muted/40 border border-border rounded p-3" data-testid="register-platform-liability-disclaimer">
                  <strong>Disclaimer:</strong> ProcureOne Ultra acts as a procurement facilitation platform. Verification of supplier qualifications, licenses, certifications, statutory compliance and suitability remains the responsibility of the buyer. You will be able to complete your full Capability Profile (experience, services offered, licenses) from your dashboard after registration.
                </div>
              </>
            )}

            <button type="submit" disabled={loading || !consent} className="w-full px-6 py-3 rounded-md bg-navy text-white font-semibold hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed" data-testid="register-submit-btn">
              {loading
                ? 'Processing…'
                : (role === 'supplier' ? `Create Account — Continue to Pay ₹${SUPPLIER_FEE.total.toLocaleString('en-IN', { minimumFractionDigits: 2 })}` : 'Create Account')}
            </button>
            <div className="pt-3 border-t border-border">
              <ConsentCheckbox checked={consent} onChange={setConsent} testid="register-consent" />
            </div>
          </form>

          <p className="mt-6 text-center text-sm text-muted-foreground">
            Already have an account?{' '}
            <Link to="/login" className="font-medium text-navy hover:underline" data-testid="login-link">
              Sign in here
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

const Row = ({ label, value, bold, testid }) => (
  <div className="flex justify-between text-sm" data-testid={testid}>
    <span className={bold ? 'font-bold text-navy' : 'text-muted-foreground'}>{label}</span>
    <span className={bold ? 'font-bold text-navy text-base' : ''}>{value}</span>
  </div>
);

// Cross-browser clipboard copy with graceful fallback so the sandboxed preview
// (where the async Clipboard API can be blocked) doesn't throw an unhandled error.
async function copyToClipboard(text) {
  try {
    if (typeof navigator !== 'undefined' && navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch { /* fall through */ }
  try {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.setAttribute('readonly', '');
    ta.style.position = 'fixed'; ta.style.top = '-1000px'; ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    return ok;
  } catch { return false; }
}

const InfoRow = ({ label, value, testid, mono, bold, icon, action }) => (
  <div className="rounded-lg bg-white border border-navy/10 p-3" data-testid={testid}>
    <div className="text-[10px] uppercase tracking-wider text-navy/60 font-semibold flex items-center gap-1">
      {icon}
      {label}
    </div>
    <div className="mt-1 flex items-center justify-between gap-2">
      <div className={`${mono ? 'font-mono' : ''} ${bold ? 'font-bold' : 'font-medium'} text-navy break-all text-sm`}>{value}</div>
      {action && (
        <button
          type="button"
          onClick={action.onClick}
          className="inline-flex items-center gap-1 px-2 py-1 rounded text-[11px] font-semibold bg-gold text-navy hover:bg-orange hover:text-white transition-colors flex-shrink-0"
          data-testid={action.testid}
        >
          {action.icon} {action.label}
        </button>
      )}
    </div>
  </div>
);

export default Register;
